import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { ArrowLeft, ImageIcon, Sparkles, X } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useSession } from "@/hooks/use-session";
import { useToast } from "@/components/system";
import { DOCUMENT_CATEGORIES, setDocumentNumber } from "@/hooks/use-documents";
import { CaptureModePicker } from "@/components/documents/CaptureModePicker";
import { syncDocumentToCalendarEvents } from "@/lib/document-calendar-events";
import {
  FormInput,
  FormSelect,
  FormTextarea,
  PrimaryButton,
  SecondaryButton,
  Row,
} from "@/components/forms";
import { formatError } from "@/lib/format-error";

/**
 * BW10 — /documents/new. Two paths:
 *   1. Take or upload a photo → Anthropic vision extraction → review form
 *   2. Skip extraction and fill the form manually
 *
 * Submit creates the row, then writes the encrypted number via document_set_number.
 */

const WORKER_URL =
  (import.meta.env.VITE_HAUS_WORKER_URL as string | undefined) ??
  "https://haus-worker.rackljo.workers.dev";

type Extraction = {
  category: string;
  kind: string;
  type_confidence: number | null;
  title: string;
  issuer: string | null;
  document_number: string | null;
  issued_at: string | null;
  expires_at: string | null;
  issued_country: string | null;
  metadata: Record<string, unknown>;
  field_confidences: Record<string, "high" | "medium" | "low">;
  cost_usd?: number;
};

export function DocumentNew() {
  const toast = useToast();
  const navigate = useNavigate();
  const { session } = useSession();
  const userId = session?.user?.id;

  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [category, setCategory] = useState("identity");
  const [kind, setKind] = useState("");
  const [title, setTitle] = useState("");
  const [issuer, setIssuer] = useState("");
  const [number, setNumber] = useState("");
  const [issuedAt, setIssuedAt] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [country, setCountry] = useState("");
  const [metadata, setMetadata] = useState<string>("{}");
  const [notes, setNotes] = useState("");
  // BW20.4b: surfaced from /api/documents/extract.
  const [typeConfidence, setTypeConfidence] = useState<number | null>(null);
  const [fieldConfidences, setFieldConfidences] = useState<
    Record<string, "high" | "medium" | "low">
  >({});

  const extract = useMutation({
    mutationFn: async (): Promise<Extraction> => {
      if (!imageFile) throw new Error("Pick an image first");
      const { data: { session: s } } = await supabase.auth.getSession();
      if (!s?.access_token) throw new Error("Not signed in");
      const form = new FormData();
      form.append("image", imageFile);
      const resp = await fetch(`${WORKER_URL}/api/documents/extract-from-photo`, {
        method: "POST",
        headers: { Authorization: `Bearer ${s.access_token}` },
        body: form,
      });
      if (!resp.ok) {
        const t = await resp.text();
        throw new Error(`${resp.status}: ${t.slice(0, 200)}`);
      }
      return (await resp.json()) as Extraction;
    },
    onSuccess: (x) => {
      setCategory(x.category || "identity");
      setKind(x.kind || "");
      setTitle(x.title || "");
      setIssuer(x.issuer ?? "");
      setNumber(x.document_number ?? "");
      setIssuedAt(x.issued_at ?? "");
      setExpiresAt(x.expires_at ?? "");
      setCountry(x.issued_country ?? "");
      setMetadata(JSON.stringify(x.metadata ?? {}, null, 2));
      setTypeConfidence(x.type_confidence);
      setFieldConfidences(x.field_confidences ?? {});
      toast.show(`Extracted${x.cost_usd ? ` · $${x.cost_usd.toFixed(3)}` : ""}.`);
    },
    onError: (e) => toast.show(formatError(e)),
  });

  const create = useMutation({
    mutationFn: async () => {
      if (!userId) throw new Error("Not signed in");
      let parsedMeta: Record<string, unknown> = {};
      if (metadata.trim()) {
        try {
          parsedMeta = JSON.parse(metadata);
        } catch {
          throw new Error("Metadata must be valid JSON");
        }
      }
      const insert = (supabase as unknown as {
        from: (t: string) => ReturnType<typeof supabase.from>;
      }).from("document");
      const { data, error } = await insert
        .insert({
          user_id: userId,
          category,
          kind: kind || category,
          title,
          issuer: issuer || null,
          issued_at: issuedAt || null,
          expires_at: expiresAt || null,
          issued_country: country || null,
          metadata: parsedMeta,
          notes,
        } as never)
        .select()
        .single();
      if (error) throw error;
      const row = data as unknown as { id: string };
      if (number) {
        await setDocumentNumber(row.id, number);
      }
      // BW20.4c: travel kinds fan out to calendar_event rows linked to
      // the document. Cascade delete handles cleanup; on edit (handled
      // in DocumentDetail) the same helper is called with wipeFirst=true.
      const eventResult = await syncDocumentToCalendarEvents({
        id: row.id,
        user_id: userId,
        kind: kind || category,
        title: title.trim(),
        metadata: parsedMeta,
      });
      return { id: row.id, events_created: eventResult.created };
    },
    onSuccess: (out) => {
      const evMsg =
        out.events_created > 0
          ? ` ${out.events_created} calendar event${out.events_created === 1 ? "" : "s"} created.`
          : "";
      toast.show(`Document added.${evMsg}`);
      navigate(`/documents/${out.id}`);
    },
    onError: (e) => toast.show(formatError(e)),
  });

  function pickImage(file: File | null) {
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImageFile(file);
    setImagePreview(file ? URL.createObjectURL(file) : null);
  }

  return (
    <article>
      <button
        type="button"
        onClick={() => navigate("/documents")}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
          background: "transparent",
          color: "var(--fg-3)",
          fontSize: "var(--type-body-s)",
          border: "none",
          padding: 0,
          cursor: "pointer",
          marginBottom: "var(--space-3)",
        }}
      >
        <ArrowLeft size={14} strokeWidth={1.5} /> Documents
      </button>
      <h1
        style={{
          fontFamily: "var(--font-serif)",
          fontStyle: "italic",
          fontWeight: 500,
          fontSize: "var(--type-display-m)",
          margin: "var(--space-2) 0 var(--space-6)",
        }}
      >
        New document.
      </h1>

      <section
        style={{
          padding: "var(--space-4)",
          background: "var(--bg-2)",
          border: "1px solid var(--hair)",
          borderRadius: "var(--radius-card)",
          marginBottom: "var(--space-4)",
          display: "grid",
          gap: "var(--space-3)",
        }}
      >
        <p style={{ margin: 0, color: "var(--fg-2)", fontSize: "var(--type-body-s)" }}>
          Snap or upload a photo of the document. Claude extracts type, number, dates, and issuer
          for you to review. ~$0.01 per call.
        </p>
        {imagePreview ? (
          <div style={{ position: "relative", borderRadius: "var(--radius-card)", overflow: "hidden" }}>
            <img
              src={imagePreview}
              alt="Document preview"
              style={{ width: "100%", maxHeight: 280, objectFit: "cover", display: "block" }}
            />
            <button
              type="button"
              onClick={() => pickImage(null)}
              aria-label="Remove image"
              style={{
                position: "absolute",
                top: 8,
                right: 8,
                background: "rgba(10,11,13,0.75)",
                color: "var(--fg)",
                border: "none",
                borderRadius: "var(--radius-chip)",
                padding: 6,
                cursor: "pointer",
                display: "inline-flex",
              }}
            >
              <X size={14} strokeWidth={1.6} />
            </button>
          </div>
        ) : (
          <label
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "10px 14px",
              background: "var(--bg-3)",
              border: "1px solid var(--hair-2)",
              borderRadius: "var(--radius-card)",
              color: "var(--fg)",
              fontSize: "var(--type-body-s)",
              cursor: "pointer",
              alignSelf: "flex-start",
            }}
          >
            <ImageIcon size={14} strokeWidth={1.5} /> Choose image
            <input
              type="file"
              accept="image/*"
              capture="environment"
              hidden
              onChange={(e) => pickImage(e.target.files?.[0] ?? null)}
            />
          </label>
        )}
        {imageFile && !extract.isPending && (
          <PrimaryButton onClick={() => extract.mutate()}>
            <Sparkles size={14} strokeWidth={1.6} style={{ marginRight: 6 }} />
            Analyze image
          </PrimaryButton>
        )}
        {extract.isPending && <p style={{ margin: 0, color: "var(--fg-3)" }}>Analyzing…</p>}
      </section>

      <section
        style={{
          padding: "var(--space-4)",
          background: "var(--bg-2)",
          border: "1px solid var(--hair)",
          borderRadius: "var(--radius-card)",
          marginBottom: "var(--space-4)",
        }}
      >
        <CaptureModePicker
          category={category}
          kind={kind}
          title={title}
          onSaved={(id) => navigate(`/documents/${id}`)}
        />
      </section>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate();
        }}
        style={{ display: "grid", gap: "var(--space-3)" }}
      >
        {typeConfidence != null && typeConfidence < 0.6 && (
          <div
            role="status"
            style={{
              padding: "var(--space-3)",
              background: "color-mix(in oklab, var(--amber) 12%, transparent)",
              border: "1px solid var(--amber)",
              borderRadius: "var(--radius-card)",
              fontSize: "var(--type-body-s)",
              color: "var(--fg)",
              lineHeight: 1.5,
            }}
          >
            Couldn't detect type confidently. Please pick a category and kind below.
          </div>
        )}
        <Row>
          <FormSelect
            label="Category"
            value={category}
            onChange={setCategory}
            options={DOCUMENT_CATEGORIES}
          />
          <FormInput
            label="Kind"
            value={kind}
            onChange={setKind}
            placeholder="passport, flight_ticket, gym_card…"
          />
        </Row>
        <FieldWithConfidence label="Title" confidence={fieldConfidences.title}>
          <FormInput label="Title" value={title} onChange={setTitle} required />
        </FieldWithConfidence>
        <FieldWithConfidence label="Issuer" confidence={fieldConfidences.issuer}>
          <FormInput label="Issuer" value={issuer} onChange={setIssuer} />
        </FieldWithConfidence>
        <FieldWithConfidence
          label="Document number"
          confidence={fieldConfidences.document_number}
        >
          <FormInput
            label="Document number (encrypted at rest)"
            value={number}
            onChange={setNumber}
            placeholder="Optional"
          />
        </FieldWithConfidence>
        <Row>
          <FieldWithConfidence label="Issued" confidence={fieldConfidences.issued_at}>
            <FormInput label="Issued" type="date" value={issuedAt} onChange={setIssuedAt} />
          </FieldWithConfidence>
          <FieldWithConfidence label="Expires" confidence={fieldConfidences.expires_at}>
            <FormInput label="Expires" type="date" value={expiresAt} onChange={setExpiresAt} />
          </FieldWithConfidence>
        </Row>
        <FormInput
          label="Country (ISO-2)"
          value={country}
          onChange={setCountry}
          placeholder="DE, US, CH…"
        />
        <FormTextarea
          label="Metadata (JSON)"
          value={metadata}
          onChange={setMetadata}
          rows={4}
          placeholder='{"PNR":"ABC123","seat":"14C"}'
        />
        <FormTextarea label="Notes" value={notes} onChange={setNotes} rows={2} />
        <div style={{ display: "flex", justifyContent: "flex-end", gap: "var(--space-2)" }}>
          <SecondaryButton onClick={() => navigate("/documents")} disabled={create.isPending}>
            Cancel
          </SecondaryButton>
          <PrimaryButton type="submit" disabled={create.isPending || !title.trim()}>
            {create.isPending ? "Saving…" : "Save document"}
          </PrimaryButton>
        </div>
      </form>
    </article>
  );
}

function FieldWithConfidence({
  confidence,
  children,
}: {
  label: string;
  confidence?: "high" | "medium" | "low";
  children: React.ReactNode;
}) {
  if (!confidence) return <>{children}</>;
  const color =
    confidence === "high"
      ? "var(--accent)"
      : confidence === "medium"
        ? "var(--amber)"
        : "var(--brick)";
  return (
    <div style={{ position: "relative", display: "block" }}>
      {children}
      <span
        title={`Extracted with ${confidence} confidence`}
        style={{
          position: "absolute",
          top: 4,
          right: 6,
          padding: "1px 6px",
          background: `color-mix(in oklab, ${color} 14%, transparent)`,
          color,
          fontSize: "var(--type-micro)",
          fontWeight: 600,
          borderRadius: "var(--radius-chip)",
          letterSpacing: "0.04em",
          textTransform: "uppercase",
        }}
      >
        {confidence}
      </span>
    </div>
  );
}
